import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { supabase } from '@/integrations/supabase/client';
import { createRecommendationTrackingWrapper } from './createRecommendationTrackingWrapper';
import { MessageDisplay } from '../../types/chat-hook-types';

/**
 * Task creation with recommendation tracking
 * Now uses create_task_simple RPC function for personal tasks (no project dependencies)
 * Automatically assigns the user as the task assignee
 */
export const useAgentChatTaskCreationWithTracking = (message: MessageDisplay | string) => {
  const { user } = useUser();
  
  console.log('[useAgentChatTaskCreationWithTracking] 🚀 Hook called with message:', typeof message === 'string' ? message : { id: message.id, databaseId: message.databaseId });
  
  // Create the base task mutation hook that uses create_task_simple RPC
  const usePersonalTaskMutation = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
      mutationFn: async (taskData: { title: string; description: string; dueDate?: string; priority?: string }) => {
        if (!user?.id) {
          throw new Error('User not authenticated');
        }

        console.log('[PersonalTaskMutation] Creating task with RPC:', taskData);

        // Use create_task_simple RPC function (same as Task Manager)
        const { data, error } = await supabase.rpc('create_task_simple', {
          p_title: taskData.title,
          p_description: taskData.description || null,
          p_due_date: taskData.dueDate || null,
          p_priority: taskData.priority || null,
          p_urgency: null
        });

        if (error) {
          console.error('[PersonalTaskMutation] RPC error:', error);
          throw new Error(error.message);
        }

        if (!data) {
          throw new Error('No response from server');
        }

        // Parse JSON response
        const result = typeof data === 'string' ? JSON.parse(data) : data;

        if (!result.success) {
          throw new Error(result.error || 'Task creation failed');
        }

        console.log('[PersonalTaskMutation] Task created successfully:', result.task);

        // Add user as assignee to the created task
        try {
          const { error: assigneeError } = await supabase.rpc('add_task_assignee', {
            p_task_id: result.task.id,
            p_assignee_id: user.id
          });

          if (assigneeError) {
            console.error('[PersonalTaskMutation] Failed to add assignee, but task was created:', assigneeError);
            // Don't fail the whole operation for assignee issues
          } else {
            console.log('[PersonalTaskMutation] User assigned to task successfully');
          }
        } catch (assigneeError) {
          console.error('[PersonalTaskMutation] Exception adding assignee:', assigneeError);
          // Don't fail the whole operation for assignee issues
        }

        return result.task;
      },
      onSuccess: () => {
        // Invalidate task-related queries
        queryClient.invalidateQueries({ 
          queryKey: ['tasks', user?.id],
          exact: false 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['task-manager-tasks', user?.id],
          exact: false 
        });
      }
    });
  };
  
  console.log('[useAgentChatTaskCreationWithTracking] 🔍 Creating tracking wrapper...');
  
  // Apply tracking wrapper
  const trackingWrapper = createRecommendationTrackingWrapper(
    usePersonalTaskMutation,
    'task',
    {
      // Simplified metadata for personal task tracking (no project dependencies)
      getMetadata: (params, result) => {
        console.log('[TaskTracking] Starting metadata generation for personal task:', params);
        
        const metadata = {
          taskTitle: params.title || 'Untitled Task',
          taskDescription: params.description || '',
          priority: params.priority || null,
          urgency: params.urgency || null
          // No goalId or projectId - personal tasks only
        };
        
        console.log('[TaskTracking] Final metadata:', metadata);
        return metadata;
      },
      // Use the task ID from the result
      getSavedItemId: (result) => result.id
    }
  );
  
  console.log('[useAgentChatTaskCreationWithTracking] ✅ Tracking wrapper created');
  
  const result = trackingWrapper(message);
  
  console.log('[useAgentChatTaskCreationWithTracking] 🔍 Wrapper result:', {
    hasFunction: !!result.mutateWithTracking,
    functionType: typeof result.mutateWithTracking
  });
  
  return result;
}; 